import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect,
} from "react-router-dom";
import { PERMISSIONS, useUser } from "./core/context/UserContext";
import LoginPage from "./pages/LoginPage";
import UserDataGridPage from "./pages/user/UserDataGridPage";
import ReportTemplateDataGridPage from "./pages/report-template/ReportTemplateDataGridPage";
import ReportTemplateFormPage from "./pages/report-template/ReportTemplateFormPage";
import ReportOutputTemplatePage from "./pages/report-output-template/ReportOutputTemplatePage";
import StudyGridPage from "./pages/study/StudyGridPage";
import ReportModal from "./pages/report/ReportModal";
import HospitalGridPage from "./pages/hospital/HospitalGridPage";
import MainLayout from "./core/components/layout/main-layout/MainLayout";
import ReportDataGridPage from "./pages/report/ReportDataGridPage";
import ReportBillingPage from "./pages/report-billing/ReportBillingPage";
import ReportBillingDuplicatesPage from "./pages/report-billing/ReportBillingDuplicatesPage";
import ReAssessmentPage from "./pages/re-assessment/ReAssessmentPage";
import StudyListPage from "./pages/study-list/StudyListPage";
import HospitalCreateEditPage from "./pages/hospital/HospitalCreateEditPage";
import SystemRequestTypePage from "./pages/request-type/SystemRequestTypePage";
import RoleManagerPage from "./pages/role/RoleManagerPage";
import UserCreateEditPage from "./pages/user/UserCreateEditPage";
import ReportPreviewPage from "./pages/report/ReportPreviewPage";
import HospitalUserPermissionPage from "./pages/hospital/HospitalUserPermissionPage";
import UserDictionaryPage from "./pages/user/UserDictionaryPage";
import HospitalGroupEditPage from "./pages/hospital-group/HospitalGroupEditPage";
import { SignalRProvider } from "./core/context/SignalRContext";
import Modal from "./core/components/Modal";
import DashboardPage from "./pages/dashboard/DashboardPage";
import HL7GridPage from "./pages/hl7/HL7GridPage";
import TeletipGridPage from "./pages/teletip/TeletipGridPage";
import DeviceGridPage from "./pages/device/DeviceGridPage";
import DeviceCreateEditPage from "./pages/device/DeviceCreateEditPage";
import WorklistItemGridPage from "./pages/worklist-item/WorklistItemGridPage";
import WorklistItemCreateEditPage from "./pages/worklist-item/WorklistItemCreateEditPage";
import WorklistSchedulerPage from "./pages/worklist-item/WorklistSchedulerPage";
import AppointmentSchedulerPage from "./pages/appointment/AppointmentSchedulerPage";
import NotFoundPage from "./pages/NotFoundPage";
import LogGridPage from "./pages/log/LogGridPage";
import AppLogViewerPage from "./pages/log/AppLogViewerPage";
import NotificationAdminPage from "./pages/notification/NotificationAdminPage";
import AutoExportRuleGridPage from "./pages/auto-export/AutoExportRuleGridPage";
import ViewerSettingsPage from "./pages/viewer-settings/ViewerSettingsPage";
import SettingsPage from "./pages/settings/SettingsPage";

export const Pages = {
    Dashboard: {
        Home: `/app/dashboard`,
    },
    Auth: {
        Logout: '/app/authentication/logout',
        Login: '/app/authentication/login'
    },
    Study: {
        Study: '/app/study',
        Viewer: `/app/study/viewer`,
        Report: (studyId) => `/app/study/${studyId}/requests`,
        ReportEdit: (studyId, requestId, reportId) => `/app/study/${studyId}/requests/${requestId}/report/${reportId}`,
        ReportPreview: (studyId, requestId, reportId) => `/app/study/${studyId}/requests/${requestId}/report/${reportId}/preview`,
    },
    Hospital: {
        Hospital: '/app/hospitals',
        Edit: (id) => `/app/hospitals/${id}/edit`,
        Create: `/app/hospitals/create`,
        UserPermissions: (id) => `/app/hospitals/${id}/users`,
    },
    HL7: {
        DataGrid: `/app/hl7/messages`
    },
    PacsSystem: {
        Create: (hospitalId) => `/app/hospital/${hospitalId}/pacs/create`,
        Edit: (hospitalId, pacsSystemId) => `/app/hospital/${hospitalId}/pacs/${pacsSystemId}/`,
    },
    HospitalGroup: {
        HospitalGroupEdit: `/app/hospitals/groups`
    },
    User: {
        DataGrid: '/app/users',
        Create: `/app/users/create`,
        Edit: (userId) => `/app/users/${userId}/edit`,
        UserHospitals: (id) => `/app/users/${id}/hospitals`,
        UserPermissions: (id) => `/app/users/${id}/permissions`,
        Dictionary: `/app/users/dictionary`
    },
    ReportTemplate: {
        Create: `/app/report/templates/create`,
        Edit: (id) => `/app/report/templates/${id}/edit`,
        DataGrid: `/app/report/templates`
    },
    ReportOutputTemplate: {
        // Hastane rapor çıktı şablonları (builder)
        Builder: `/app/report/output-templates`,
        Edit: (hospitalId) => `/app/report/output-templates/${hospitalId}`
    },
    Report: {
        DataGrid: `/app/reports/grid`
    },
    ReportBilling: {
        DataGrid: `/app/report-billing`,
        Duplicates: `/app/report-billing/duplicates`,
        ReAssessment: `/app/report-billing/re-assessment`
    },
    StudyList: {
        Manage: `/app/study-lists`
    },
    RequestType: {
        System: `/app/request-types`
    },
    Settings: {
        Home: `/app/settings`
    },
    ViewerSettings: {
        Settings: `/app/viewer-settings`
    },
    Role: {
        Manager: `/app/roles`
    },
    Download: {
        download: `/app/download`
    },
    Teletip: {
        DataGrid: `/app/teletip/grid`
    },
    Device: {
        DataGrid: '/app/devices',
        Create: `/app/devices/create`,
        Edit: (id) => `/app/devices/${id}/edit`,
    },
    WorklistItem: {
        DataGrid: '/app/worklist-items',
        Create: `/app/worklist-items/create`,
        Edit: (id) => `/app/worklist-items/${id}/edit`,
    },
    Appointment: {
        Scheduler: '/app/appointment-scheduler',
    },
    Log: {
        DataGrid: '/app/logs',
        AppLogs: '/app/app-logs'
    },
    Notification: {
        Manage: '/app/notifications'
    },
    AutoExport: {
        Rules: '/app/auto-export/rules'
    }
}

function PrivateRoute({ children, ...rest }) {
    const {
        isAuth
    } = useUser();

    return (
        <Route {...rest} render={() => {
            return isAuth()
                ? children
                : <Redirect to={Pages.Auth.Login} />
        }} />
    )
}

// Tüm uygulama route'ları tek yerde. Hem klasik <Switch> hem de sekmeli
// (keep-alive) outlet için yeniden kullanılır. Yetkisiz olanlar false döner
// ve <Switch> tarafından yok sayılır.
function getAppRoutes(hasPermission, showDashboard) {
    return [
        <Route key="root" exact path={'/'} component={showDashboard() ? DashboardPage : StudyGridPage} />,
        showDashboard() && (<Route key="dashboard" exact path={Pages.Dashboard.Home} component={DashboardPage} />),

        <Route key="user-dictionary" exact path={Pages.User.Dictionary} component={UserDictionaryPage} />,
        hasPermission(PERMISSIONS.USER_MANAGER) && (<Route key="user-create" exact path={Pages.User.Create} component={UserCreateEditPage} />),
        hasPermission(PERMISSIONS.USER_MANAGER) && (<Route key="user-edit" exact path={Pages.User.Edit(':userId')} component={UserCreateEditPage} />),
        hasPermission(PERMISSIONS.USER_MANAGER) && (<Route key="user-grid" exact path={Pages.User.DataGrid} component={UserDataGridPage} />),

        hasPermission(PERMISSIONS.REPORT_TEMPLATE_MANAGER) && (<Route key="rt-create" exact path={Pages.ReportTemplate.Create} component={ReportTemplateFormPage} />),
        hasPermission(PERMISSIONS.REPORT_TEMPLATE_MANAGER) && (<Route key="rt-edit" exact path={Pages.ReportTemplate.Edit(':id')} component={ReportTemplateFormPage} />),
        hasPermission(PERMISSIONS.REPORT_TEMPLATE_MANAGER) && (<Route key="rt-grid" exact path={Pages.ReportTemplate.DataGrid} component={ReportTemplateDataGridPage} />),

        hasPermission(PERMISSIONS.REPORT_OUTPUT_TEMPLATE_MANAGER) && (<Route key="rot-builder" exact path={Pages.ReportOutputTemplate.Builder} component={ReportOutputTemplatePage} />),
        hasPermission(PERMISSIONS.REPORT_OUTPUT_TEMPLATE_MANAGER) && (<Route key="rot-edit" exact path={Pages.ReportOutputTemplate.Edit(':hospitalId')} component={ReportOutputTemplatePage} />),

        hasPermission(PERMISSIONS.STUDY_LIST) && (<Route key="study" exact path={Pages.Study.Study} component={StudyGridPage} />),
        hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (<Route key="study-report" exact path={Pages.Study.Report(':studyId')} component={ReportModal} />),
        hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (<Route key="study-report-edit" exact path={Pages.Study.ReportEdit(':studyId', ':requestId', ':reportId')} component={ReportModal} />),
        hasPermission(PERMISSIONS.REPORT_LIST) && (<Route key="study-report-preview" exact path={Pages.Study.ReportPreview(':studyId', ':requestId', ':reportId')} component={ReportPreviewPage} />),

        hasPermission(PERMISSIONS.HOSPITAL_MANAGER) && (<Route key="hospital" exact path={Pages.Hospital.Hospital} component={HospitalGridPage} />),
        hasPermission(PERMISSIONS.HOSPITAL_MANAGER) && (<Route key="hospital-create" exact path={Pages.Hospital.Create} component={HospitalCreateEditPage} />),
        hasPermission(PERMISSIONS.HOSPITAL_MANAGER) && (<Route key="hospital-users" exact path={Pages.Hospital.UserPermissions(':hospitalId')} component={HospitalUserPermissionPage} />),
        hasPermission(PERMISSIONS.HOSPITAL_MANAGER) && (<Route key="hospital-edit" exact path={Pages.Hospital.Edit(':hospitalId')} component={HospitalCreateEditPage} />),
        hasPermission(PERMISSIONS.HL7_MANAGER) && (<Route key="hl7" exact path={Pages.HL7.DataGrid} component={HL7GridPage} />),
        hasPermission(PERMISSIONS.TELETIP_MANAGER) && (<Route key="teletip" exact path={Pages.Teletip.DataGrid} component={TeletipGridPage} />),

        hasPermission(PERMISSIONS.HOSPITAL_GROUP_MANAGER) && (<Route key="hospital-group" exact path={Pages.HospitalGroup.HospitalGroupEdit} component={HospitalGroupEditPage} />),
        hasPermission(PERMISSIONS.DEVICE_LIST) && (<Route key="device" exact path={Pages.Device.DataGrid} component={DeviceGridPage} />),
        hasPermission(PERMISSIONS.DEVICE_CREATE_EDIT) && (<Route key="device-create" exact path={Pages.Device.Create} component={DeviceCreateEditPage} />),
        hasPermission(PERMISSIONS.DEVICE_CREATE_EDIT) && (<Route key="device-edit" exact path={Pages.Device.Edit(':deviceId')} component={DeviceCreateEditPage} />),

        hasPermission(PERMISSIONS.WORKLIST_LIST) && (<Route key="worklist" exact path={Pages.WorklistItem.DataGrid} component={WorklistItemGridPage} />),
        hasPermission(PERMISSIONS.WORKLIST_CREATE_EDIT) && (<Route key="worklist-create" exact path={Pages.WorklistItem.Create} component={WorklistItemCreateEditPage} />),
        hasPermission(PERMISSIONS.WORKLIST_CREATE_EDIT) && (<Route key="worklist-edit" exact path={Pages.WorklistItem.Edit(':worklistItemId')} component={WorklistItemCreateEditPage} />),
        hasPermission(PERMISSIONS.WORKLIST_CREATE_EDIT) && (<Route key="appointment" exact path={Pages.Appointment.Scheduler} component={AppointmentSchedulerPage} />),
        hasPermission(PERMISSIONS.REQUEST_TYPE_MANAGER) && (<Route key="request-type" exact path={Pages.RequestType.System} component={SystemRequestTypePage} />),
        hasPermission(PERMISSIONS.USER_MANAGER) && (<Route key="role" exact path={Pages.Role.Manager} component={RoleManagerPage} />),

        hasPermission(PERMISSIONS.REPORT_LIST) && (<Route key="report-grid" exact path={Pages.Report.DataGrid} component={ReportDataGridPage} />),
        (hasPermission(PERMISSIONS.REPORT_BILLING_LIST) && hasPermission(PERMISSIONS.REPORT_BILLING_DUPLICATE_DETECT)) && (<Route key="report-billing-duplicates" exact path={Pages.ReportBilling.Duplicates} component={ReportBillingDuplicatesPage} />),
        hasPermission(PERMISSIONS.RE_ASSESSMENT_MANAGE) && (<Route key="re-assessment" exact path={Pages.ReportBilling.ReAssessment} component={ReAssessmentPage} />),
        hasPermission(PERMISSIONS.REPORT_BILLING_LIST) && (<Route key="report-billing" exact path={Pages.ReportBilling.DataGrid} component={ReportBillingPage} />),
        // Listelerim: liste oluşturabilen ya da en az bir liste görebilen herkes.
        (hasPermission(PERMISSIONS.LIST_CREATE) || hasPermission(PERMISSIONS.LIST_VIEW) || hasPermission(PERMISSIONS.LIST_VIEW_ALL))
            && (<Route key="study-lists" exact path={Pages.StudyList.Manage} component={StudyListPage} />),
        hasPermission(PERMISSIONS.LOG_MANAGEMENT) && (<Route key="logs" exact path={Pages.Log.DataGrid} component={LogGridPage} />),
        hasPermission(PERMISSIONS.LOG_MANAGEMENT) && (<Route key="app-logs" exact path={Pages.Log.AppLogs} component={AppLogViewerPage} />),
        hasPermission(PERMISSIONS.NOTIFICATION_MANAGE) && (<Route key="notifications" exact path={Pages.Notification.Manage} component={NotificationAdminPage} />),
        hasPermission(PERMISSIONS.AUTO_EXPORT_MANAGE) && (<Route key="auto-export" exact path={Pages.AutoExport.Rules} component={AutoExportRuleGridPage} />),
        hasPermission(PERMISSIONS.VIEWER_SETTINGS_MANAGER) && (<Route key="viewer-settings" exact path={Pages.ViewerSettings.Settings} component={ViewerSettingsPage} />),
        (hasPermission(PERMISSIONS.BRANDING_MANAGE) || hasPermission(PERMISSIONS.CONFIG_MANAGE))
            && (<Route key="settings" exact path={Pages.Settings.Home} component={SettingsPage} />),
        <Route key="notfound" component={NotFoundPage} />,
    ];
}

export default function AppRouter() {
    const { hasPermission, showDashboard } = useUser();
    return (
        <>
            <Router>
                <Switch>
                    <Route path={Pages.Auth.Login} component={LoginPage} />
                    <PrivateRoute>
                        <SignalRProvider>
                            <MainLayout>
                                <Switch>
                                    {getAppRoutes(hasPermission, showDashboard)}
                                </Switch>
                            </MainLayout>
                            {/* Modal içerikleri SignalR bağlamının İÇİNDE olsun: uzun işlemlerin
                                canlı ilerlemesi (hasta güncelleme, çekim birleştirme) modalde
                                gösteriliyor. Eskiden App.js'te, sağlayıcının dışındaydı. */}
                            <Modal />
                        </SignalRProvider>
                    </PrivateRoute>
                </Switch>
            </Router>
        </>
    )
}
